import { ToolbarButton } from "./ToolbarButton";
import { ToolbarButtonToggle } from "./ToolbarButtonToggle";
import { ToolbarButtonLink } from "./ToolbarButtonLink";
import { ToolbarSelect } from "./ToolbarSelect";
import { ToolbarItemProps, ToolbarOverflowTypes, ToolbarSize } from "./types";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LayoutUtilProps } from "../../../types";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import {
  handleToolbarKeyDown,
  updateToolbarItemsTabIndex,
} from "./internal/utils/accessibility";
import { ToolbarContext } from "./internal/ToolbarContext";
import Flex from "../../../components/Flex";
import { useMergeRefs } from "../../../hooks";
import { ToolbarOverflowMenu } from "./internal/ToolbarOverflowMenu";
import { FilterGroup } from "./Filters/FilterGroup";
import { ToolbarControlGroup } from "./ToolbarControlGroup";
import { ToolbarSearchField } from "./ToolbarSearchField";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Props for the Toolbar component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type ToolbarProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Description of the content this toolbar is associated with, used for accessibility
     */
    associatedContent: string;
    /**
     * Orientation of the toolbar
     * @default horizontal
     */
    direction?: "vertical" | "horizontal";
    /**
     * How to handle overflow when items don't fit
     * @default wrap
     */
    overflow?: ToolbarOverflowTypes;
    /**
     * Additional items to display in the overflow menu
     */
    additionalItems?: ReactElement[];
    /**
     * Size of toolbar items
     * @default xsmall
     */
    size?: ToolbarSize;
  };

/**
 * Toolbar component for grouping related interactive elements with anvil2 tracking.
 *
 * Features:
 * - Horizontal or vertical orientation
 * - Overflow handling with wrap or collapse behavior
 * - Keyboard navigation following ARIA best practices
 * - Screen reader announcements and accessibility
 * - Automatic overflow menu for collapsed items
 * - Support for buttons, toggles, selects, and links
 * - Layout utilities for positioning and spacing
 * - Context-based state management for child components
 * - Automatic tracking ID generation for analytics
 * - Integration with anvil2 tracking system
 *
 * @example
 * <Toolbar associatedContent="document editor" overflow="collapse">
 *   <Toolbar.Button icon={BoldIcon} aria-label="Bold" />
 *   <Toolbar.ButtonToggle icon={ItalicIcon} aria-label="Italic" />
 *   <Toolbar.Select
 *     items={fontOptions}
 *     selected="arial"
 *     accessibleLabel="Font family"
 *   />
 * </Toolbar>
 */
export const Toolbar = Object.assign(
  forwardRef<HTMLDivElement, ToolbarProps>(function ToolbarInner(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      associatedContent,
      className,
      children,
      style,
      direction = "horizontal",
      overflow = "wrap",
      onKeyDown,
      additionalItems,
      size = "xsmall",
      ...rest
    } = componentProps;

    // State for tracking items that should appear in the overflow menu
    const [overflowItems, setOverflowItems] = useState<ToolbarItemProps[]>([]);
    const toolbarItemsRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);

    // Track original order of items to maintain consistent ordering in the overflow menu
    const [orderedIds, setOrderedIds] = useState<string[]>([]);

    // Update ordered IDs when children change to maintain correct overflow order
    useEffect(() => {
      const newOrderedIds = Array.from(
        toolbarItemsRef.current?.querySelectorAll("[data-id]") || [],
      ).map((el) => {
        return el.getAttribute("data-id") as string;
      });

      setOrderedIds(newOrderedIds);
    }, [children]);

    // Add item to overflow menu
    const addItem = useCallback((item: ToolbarItemProps) => {
      setOverflowItems((prev) => [...prev, item]);
    }, []);

    // Remove item from overflow menu
    const removeItem = useCallback((item: ToolbarItemProps) => {
      setOverflowItems((prev) =>
        prev.filter((component) => component.id !== item.id),
      );
    }, []);

    // Initialize tabindex values
    useEffect(() => {
      if (toolbarRef.current) {
        // Initialize toolbar with the first item active
        updateToolbarItemsTabIndex(toolbarRef.current);
      }
    }, [children]);

    // Handle keyboard navigation events within the toolbar
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (toolbarRef.current) {
          handleToolbarKeyDown(event, toolbarRef.current);
        }

        onKeyDown?.(event);
      },
      [onKeyDown],
    );

    // Determine if overflow menu should be shown
    const showOverflowMenu =
      (overflow === "collapse" && overflowItems.length > 0) ||
      !!additionalItems;

    // Context value provided to all toolbar children
    const value = useMemo(
      () => ({
        overflowItems,
        addItem,
        removeItem,
        toolbarRef,
        orderedIds,
        overflow,
        size,
      }),
      [
        overflowItems,
        addItem,
        removeItem,
        toolbarRef,
        orderedIds,
        overflow,
        size,
      ],
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const toolbarClassNames = cx(styles["toolbar"], className);

    const toolbarContentClassNames = cx(styles["toolbar-content"], {
      [styles[`overflow-${overflow}`]]: overflow,
    });

    return (
      <ToolbarContext.Provider value={value}>
        <Flex
          direction={direction === "horizontal" ? "row" : "column"}
          wrap={overflow === "wrap" ? "wrap" : "nowrap"}
          alignItems="center"
          className={toolbarClassNames}
          style={styleCombined}
          ref={useMergeRefs([toolbarRef, ref])}
          role="toolbar"
          data-anv="toolbar"
          aria-orientation={direction}
          aria-label={`Toolbar for ${associatedContent}. Use arrow keys to navigate through toolbar controls.`}
          onKeyDown={handleKeyDown}
          {...rest}
        >
          <Flex
            ref={useMergeRefs([toolbarItemsRef, ref])}
            className={toolbarContentClassNames}
            direction={direction === "horizontal" ? "row" : "column"}
            wrap={overflow === "wrap" ? "wrap" : "nowrap"}
            alignItems="center"
            grow={1}
            data-anv="toolbar-content"
            data-overflow={overflow}
          >
            {children}
          </Flex>

          {showOverflowMenu && (
            <ToolbarOverflowMenu
              direction={direction}
              additionalItems={additionalItems}
            />
          )}
        </Flex>
      </ToolbarContext.Provider>
    );
  }),
  {
    /**
     * ToolbarButton component for standard clickable actions within a toolbar with anvil2 tracking.
     *
     * Features:
     * - Designed specifically for toolbar integration
     * - Consistent styling with other toolbar items
     * - Automatic tooltip for icon-only buttons
     * - Overflow menu support
     * - Accessibility enforcement for aria-labels
     * - Ghost and primary appearance options
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Toolbar.Button icon={BoldIcon} aria-label="Bold text" />
     *
     * @example
     * <Toolbar.Button appearance="primary">
     *   Save
     * </Toolbar.Button>
     */
    Button: ToolbarButton,
    /**
     * ToolbarButtonToggle component for toggleable actions within a toolbar with anvil2 tracking.
     *
     * Features:
     * - Toggle state management
     * - Designed specifically for toolbar integration
     * - Consistent styling with other toolbar items
     * - Overflow menu support
     * - Accessibility enforcement for aria-labels
     * - Ghost and primary appearance options
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Toolbar.ButtonToggle icon={BoldIcon} aria-label="Bold" checked={isBold} />
     */
    ButtonToggle: ToolbarButtonToggle,
    /**
     * ToolbarButtonLink component for link actions within a toolbar with anvil2 tracking.
     *
     * Features:
     * - Link functionality with href support
     * - Designed specifically for toolbar integration
     * - Consistent styling with other toolbar items
     * - Overflow menu support
     * - Accessibility enforcement for aria-labels
     * - Ghost and primary appearance options
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Toolbar.ButtonLink href="/help">
     *   Help
     * </Toolbar.ButtonLink>
     */
    ButtonLink: ToolbarButtonLink,
    /**
     * ToolbarSelect component provides a dropdown selector within a toolbar with anvil2 tracking.
     *
     * Features:
     * - Dropdown menu with selectable options
     * - Controlled and uncontrolled usage patterns
     * - Automatic overflow menu integration
     * - Accessibility support with proper ARIA labels
     * - Ghost and primary appearance options
     * - Dropdown arrow icon indicator
     * - Listbox integration for keyboard navigation
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Toolbar.Select
     *   items={fontOptions}
     *   selected="arial"
     *   accessibleLabel="Font family"
     *   onChange={(id) => setFont(id)}
     * />
     */
    Select: ToolbarSelect,
    /**
     * FilterGroup component displays a group of filters with toggles and a clear button.
     * Also contains the FilterDrawer for additional filter options.
     *
     * Features:
     * - Renders filter toggles and popovers for all filter types
     * - Supports controlled and uncontrolled filtering
     * - Integrates with FilterDrawer for batch filter editing
     * - Provides a clear all filters button
     * - Handles overflow and hidden filters
     * - Shares filter state via context for child components
     * - Supports boolean, custom, single-select, multi-select, date, and date range filter types
     * - Date filters include calendar popup for easy selection
     * - Date range filters support start and end date selection
     *
     * @example
     * <Toolbar.Filters
     *   filters={filters}
     *   onFilterChange={handleFilter}
     *   controlledFiltering={true}
     * />
     */
    Filters: FilterGroup,
    /**
     * ToolbarControlGroup component provides a flex container for grouping related toolbar controls.
     * Can be used for filters, buttons, or other toolbar items.
     *
     * Features:
     * - Groups related toolbar controls in a flex container
     * - Supports accessibility with aria-label
     * - Handles overflow and wrapping based on toolbar context
     * - Integrates with layout utilities for spacing and alignment
     *
     * @example
     * <Toolbar.ControlGroup aria-label="Formatting controls">
     *   <Toolbar.Button icon={BoldIcon} aria-label="Bold" />
     *   <Toolbar.Button icon={ItalicIcon} aria-label="Italic" />
     * </Toolbar.ControlGroup>
     */
    ControlGroup: ToolbarControlGroup,
    /**
     * ToolbarSearchField component renders a search field for use in toolbars.
     *
     * Features:
     * - Renders a search field styled for toolbar usage
     * - Supports all SearchField props except size
     * - Integrates with toolbar layouts and accessibility
     *
     * @example
     * <Toolbar.Search placeholder="Search..." />
     */
    Search: ToolbarSearchField,
  },
);
Toolbar.displayName = "Toolbar";
export const ToolbarElement = Toolbar;
